import { useCallback } from 'react';

import {
  buildErrorResponse,
  type ErrorResponse,
} from '@/hooks/common/useError';
import useReactQuery from '@/hooks/common/useReactQuery';
import { type Student } from '@/interfaces/student';

export interface ValidateOrganizationMembershipV2Params {
  code: string;
  first_name_kana: string;
  last_name_kana: string;
}

export interface ValidateOrganizationMembershipResult {
  student: Student;
}

interface UseOrganizationMembershipValidationV2Return {
  isLoading: boolean;
  validate: (params: ValidateOrganizationMembershipV2Params) => Promise<{
    data: ValidateOrganizationMembershipResult | null;
    error: ErrorResponse | null;
    success: boolean;
  }>;
}

const useOrganizationMembershipValidationV2 =
  (): UseOrganizationMembershipValidationV2Return => {
    const { useMutation } = useReactQuery();

    const mutation = useMutation<ValidateOrganizationMembershipResult>({
      method: 'POST',
      url: '/api/school/parents/organization_memberships_v2/validate',
    });

    const validate = useCallback(
      async (params: ValidateOrganizationMembershipV2Params) => {
        try {
          const result = await mutation.mutateAsync(JSON.stringify(params));

          // 成功レスポンスだがstudentが存在しない場合
          if (!result?.student) {
            return {
              data: null,
              error: buildErrorResponse({
                status: 400,
                message:
                  '学生情報の取得に失敗しました。団体会員コードが正しいかご確認ください。',
              }),
              success: false,
            };
          }

          return {
            data: result,
            error: null,
            success: true,
          };
        } catch (error) {
          // エラーレスポンスの場合
          if (error && typeof error === 'object' && 'status' in error) {
            const errorResponse = error as ErrorResponse;
            return {
              data: null,
              error: errorResponse,
              success: false,
            };
          }

          // ネットワークエラーやその他の例外
          return {
            data: null,
            error: buildErrorResponse({
              status: 500,
              message:
                '申し訳ありませんがサーバーへの接続中にエラーが発生しました。時間をおいて再度お試しください。',
            }),
            success: false,
          };
        }
      },
      [mutation],
    );

    return {
      isLoading: mutation.isPending,
      validate,
    };
  };

export default useOrganizationMembershipValidationV2;
